// Presence-based money redaction for the module-06 office screens (spec 06 §7).
//
// THE WIRE (verified against `apps/api/src/modules/inventory/inventory.serializer.ts`):
// module 06 does NOT drop the money keys the way module 01 drops
// `standard_cost_minor`. Its serializer always emits `unit_cost_minor` /
// `currency` / `variance_value_minor` / `est_value_minor`, and adds a sibling
// `redacted: true` marker ONLY when a value actually existed and was withheld.
// So there are three distinct facts on one field, and §7 says the UI renders
// them differently:
//
//   value !== null                    → the caller may see it → show it
//   value === null && redacted        → a cost exists, withheld from this role
//   value === null && !redacted       → genuinely UNVALUED stock (edge case 14)
//
// The doctrine rule — "the UI must never reserve an empty column" — therefore
// maps onto a PAGE-LEVEL decision, not a per-cell one: if not one row on the
// page carries a visible value, the money column is not rendered at all, and a
// one-line banner states why (withheld vs unvalued). A column of nothing but
// lock glyphs is exactly the empty column the rule forbids; a silent absence
// with no banner would be the other failure — a recall reviewer must never read
// "no cost column" as "no money at stake".
//
// (Spec 06 §4.5/§4.8 rows amended in the same PR to state this rule, because
// they were written against the module-01 "server omits the key" shape.)

/** A row carrying one of the module's redactable money fields. */
export interface MoneyBearing {
  readonly redacted?: boolean | undefined;
}

/** What a page of rows should do with its money column. */
export type CostPresence =
  /** At least one row exposes a value — render the column. */
  | "visible"
  /** No visible value anywhere, but something was withheld from this role. */
  | "withheld"
  /** No visible value and nothing withheld — the data is genuinely unvalued. */
  | "unvalued";

/**
 * Decide the money column for a page of rows. Pure + unit-tested: this is the
 * §7 redaction contract expressed once, so ten screens cannot each get it
 * subtly wrong.
 */
export function costPresence<T extends MoneyBearing>(
  rows: readonly T[],
  valueOf: (row: T) => number | null | undefined,
): CostPresence {
  let withheld = false;
  for (const row of rows) {
    const value = valueOf(row);
    if (value !== null && value !== undefined) return "visible";
    if (row.redacted === true) withheld = true;
  }
  return withheld ? "withheld" : "unvalued";
}

/** True when the money column should be rendered at all. */
export function showCostColumn(presence: CostPresence): boolean {
  return presence === "visible";
}

/** The per-cell fact, once the column is being rendered. */
export type CostCellKind = "value" | "withheld" | "unvalued";

export function costCellKind<T extends MoneyBearing>(
  row: T,
  valueOf: (row: T) => number | null | undefined,
): CostCellKind {
  const value = valueOf(row);
  if (value !== null && value !== undefined) return "value";
  return row.redacted === true ? "withheld" : "unvalued";
}

/**
 * Format integer minor units for display. Mirrors the catalogue helper (the
 * money rule is repo-wide: integer minor units, the currency's own exponent, no
 * float maths on the value itself — only the final divide for presentation).
 */
export function formatMinor(minor: number, currency: string, locale?: string): string {
  try {
    const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(minor / Math.pow(10, digits));
  } catch {
    return `${minor} ${currency}`;
  }
}
